// src/admin/AdminLayout.jsx
import { useState } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { logout } from "../services/authService";

const NAV_ITEMS = [
  { path: "/admin",           icon: "📊", label: "Dashboard",    short: "Dash"   },
  { path: "/admin/games",     icon: "🎮", label: "Games",        short: "Games"  },
  { path: "/admin/stock",     icon: "🔑", label: "Stock",        short: "Stock"  },
  { path: "/admin/orders",    icon: "📦", label: "Orders",       short: "Orders" },
  { path: "/admin/customers", icon: "👥", label: "Customers",    short: "Users"  },
  { path: "/admin/chats",     icon: "💬", label: "Support Chat", short: "Chats"  },
  { path: "/admin/settings",  icon: "⚙️",  label: "Settings",    short: "Config" },
];

const MOBILE_PRIMARY = [
  { path: "/admin",        icon: "📊", short: "Dash"   },
  { path: "/admin/orders", icon: "📦", short: "Orders" },
  { path: "/admin/stock",  icon: "🔑", short: "Stock"  },
  { path: "/admin/chats",  icon: "💬", short: "Chats"  },
  { path: "/admin/games",  icon: "🎮", short: "Games"  },
];

const MOBILE_MORE = [
  { path: "/admin/customers", icon: "👥", label: "Customers" },
  { path: "/admin/settings",  icon: "⚙️",  label: "Settings"  },
  { path: "/",                icon: "🌐", label: "View Store" },
];

const S = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
*, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }

.al-root {
  display:flex; min-height:100vh;
  background:#121212; font-family:'Inter',sans-serif;
  color:#fff; -webkit-font-smoothing:antialiased;
}

/* ── Sidebar ── */
.al-sidebar {
  background:#0d0d0d;
  border-right:1px solid rgba(255,255,255,0.06);
  display:flex; flex-direction:column; flex-shrink:0;
  position:sticky; top:0; height:100vh;
  overflow:hidden; transition:width 0.22s cubic-bezier(0.4,0,0.2,1);
}
.al-sidebar.expanded { width:220px; }
.al-sidebar.collapsed { width:60px; }

.al-logo {
  height:60px; padding:0 16px;
  display:flex; align-items:center; gap:10px;
  border-bottom:1px solid rgba(255,255,255,0.06);
  overflow:hidden; flex-shrink:0;
}
.al-logo-icon {
  width:32px; height:32px; flex-shrink:0;
  background:#0078F2; border-radius:8px;
  display:flex; align-items:center; justify-content:center;
  font-size:16px; box-shadow:0 0 16px rgba(0,120,242,0.3);
}
.al-logo-text { font-size:15px; font-weight:800; color:#fff; white-space:nowrap; overflow:hidden; }
.al-logo-text span { color:#0078F2; }

.al-nav {
  flex:1; padding:10px 8px;
  display:flex; flex-direction:column; gap:2px;
  overflow-y:auto; overflow-x:hidden;
}
.al-nav-item {
  display:flex; align-items:center; gap:10px;
  padding:10px 10px; border-radius:6px;
  font-size:13px; font-weight:500; color:#6b6b6b;
  text-decoration:none; white-space:nowrap;
  transition:all 0.12s; overflow:hidden;
}
.al-nav-item:hover { background:rgba(255,255,255,0.05); color:#c0c0c0; }
.al-nav-item.active {
  background:rgba(0,120,242,0.12); color:#0078F2;
  border-left:2px solid #0078F2;
  border-radius:0 6px 6px 0; padding-left:8px;
}
.al-nav-icon { font-size:16px; flex-shrink:0; }
.al-nav-label { overflow:hidden; font-size:13px; }
.al-nav-divider { height:1px; background:rgba(255,255,255,0.05); margin:6px 0; }

.al-sidebar-footer { padding:10px 8px; border-top:1px solid rgba(255,255,255,0.05); }
.al-collapse-btn {
  display:flex; align-items:center; gap:10px;
  padding:10px 10px; border-radius:6px; width:100%;
  font-size:13px; color:#6b6b6b; background:none; border:none;
  cursor:pointer; transition:all 0.12s; font-family:'Inter',sans-serif;
  overflow:hidden; white-space:nowrap;
}
.al-collapse-btn:hover { background:rgba(255,255,255,0.04); color:#a3a3a3; }

/* ── Main ── */
.al-main { flex:1; min-width:0; display:flex; flex-direction:column; }

.al-topbar {
  height:56px; background:#0d0d0d;
  border-bottom:1px solid rgba(255,255,255,0.06);
  padding:0 20px;
  display:flex; align-items:center; justify-content:space-between;
  position:sticky; top:0; z-index:200; flex-shrink:0;
}
.al-topbar-title { font-size:14px; font-weight:700; color:#fff; }
.al-topbar-right { display:flex; align-items:center; gap:12px; }
.al-admin-badge {
  font-size:9px; font-weight:800; color:#0078F2;
  background:rgba(0,120,242,0.1); border:1px solid rgba(0,120,242,0.25);
  border-radius:4px; padding:3px 8px; letter-spacing:1.2px; text-transform:uppercase;
}
.al-topbar-link {
  font-size:12px; font-weight:500; color:#6b6b6b;
  text-decoration:none; transition:color 0.15s;
}
.al-topbar-link:hover { color:#0078F2; }
.al-topbar-logout {
  font-size:12px; font-weight:500; color:#6b6b6b;
  background:none; border:none; cursor:pointer;
  font-family:'Inter',sans-serif; transition:color 0.15s;
}
.al-topbar-logout:hover { color:#ef4444; }

.al-content { flex:1; padding:22px; overflow-y:auto; }

/* ── Mobile bottom nav — hidden on desktop ── */
.al-bnav { display:none; }

/* ════════════════════
   MOBILE ≤ 768px
════════════════════ */
@media(max-width:768px) {
  .al-sidebar { display:none !important; }
  .al-topbar  { padding:0 14px; }
  .al-topbar-link { display:none; }
  .al-content { padding:14px 14px 80px; }

  /* Bottom nav */
  .al-bnav {
    display:flex;
    position:fixed; bottom:0; left:0; right:0;
    height:60px; z-index:1100;
    background:rgba(10,10,12,0.97);
    backdrop-filter:blur(32px);
    -webkit-backdrop-filter:blur(32px);
    border-top:1px solid rgba(255,255,255,0.07);
    align-items:stretch; justify-content:space-around;
    padding-bottom:env(safe-area-inset-bottom, 0px);
  }
  .al-bnav-tab {
    flex:1; display:flex; flex-direction:column;
    align-items:center; justify-content:center; gap:3px;
    text-decoration:none; color:#555;
    font-family:'Inter',sans-serif;
    font-size:9px; font-weight:600; letter-spacing:0.2px;
    padding:6px 2px 8px;
    -webkit-tap-highlight-color:transparent;
    transition:color 0.15s; position:relative;
    background:none; border:none; cursor:pointer;
  }
  .al-bnav-tab.active { color:#0078F2; }
  .al-bnav-tab.active::before {
    content:''; position:absolute; top:0; left:50%;
    transform:translateX(-50%);
    width:24px; height:2px;
    background:#0078F2; border-radius:0 0 3px 3px;
  }
  .al-bnav-icon {
    font-size:19px; line-height:1;
    display:flex; align-items:center; justify-content:center;
    width:24px; height:24px;
  }

  /* More drawer */
  .al-more-overlay {
    position:fixed; inset:0; z-index:1098;
    background:rgba(0,0,0,0.5);
    backdrop-filter:blur(4px);
  }
  .al-more-drawer {
    position:fixed; bottom:60px; left:0; right:0;
    background:#111; border-top:1px solid rgba(255,255,255,0.08);
    border-radius:14px 14px 0 0;
    z-index:1099; padding:6px 0;
    animation:drawerUp 0.18s cubic-bezier(0.4,0,0.2,1);
  }
  @keyframes drawerUp {
    from { opacity:0; transform:translateY(12px); }
    to   { opacity:1; transform:translateY(0); }
  }
  .al-more-row {
    display:flex; align-items:center; gap:14px;
    padding:14px 22px; font-size:14px; font-weight:500;
    color:#c0c0c0; text-decoration:none; transition:background 0.1s;
    font-family:'Inter',sans-serif; cursor:pointer;
    border:none; background:none; width:100%;
  }
  .al-more-row:hover { background:rgba(255,255,255,0.04); }
  .al-more-row.active { color:#0078F2; }
  .al-more-row-icon { font-size:18px; width:24px; text-align:center; flex-shrink:0; }
  .al-more-divider { height:1px; background:rgba(255,255,255,0.06); margin:4px 0; }
}
`;

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [showMore,  setShowMore]  = useState(false);

  async function handleLogout() { await logout(); navigate("/"); }

  const isActive = (path) =>
    path === "/admin"
      ? location.pathname === "/admin"
      : location.pathname.startsWith(path);

  const currentItem  = NAV_ITEMS.find(n => isActive(n.path));
  const moreIsActive = !MOBILE_PRIMARY.some(n => isActive(n.path));

  return (
    <>
      <style>{S}</style>
      <div className="al-root">

        {/* ── Desktop Sidebar ── */}
        <aside className={`al-sidebar ${collapsed ? "collapsed" : "expanded"}`}>
          <div className="al-logo">
            <div className="al-logo-icon">🎮</div>
            {!collapsed && <div className="al-logo-text">Phsar<span>Game</span></div>}
          </div>
          <nav className="al-nav">
            {NAV_ITEMS.map(item => (
              <Link
                key={item.path}
                to={item.path}
                className={`al-nav-item ${isActive(item.path) ? "active" : ""}`}
                title={collapsed ? item.label : ""}
              >
                <span className="al-nav-icon">{item.icon}</span>
                {!collapsed && <span className="al-nav-label">{item.label}</span>}
              </Link>
            ))}
            <div className="al-nav-divider" />
            <Link to="/" className="al-nav-item" title={collapsed ? "View Store" : ""}>
              <span className="al-nav-icon">🌐</span>
              {!collapsed && <span className="al-nav-label">View Store</span>}
            </Link>
          </nav>
          <div className="al-sidebar-footer">
            <button className="al-collapse-btn" onClick={() => setCollapsed(c => !c)}>
              <span style={{ fontSize:14, flexShrink:0 }}>{collapsed ? "→" : "←"}</span>
              {!collapsed && <span>Collapse</span>}
            </button>
          </div>
        </aside>

        {/* ── Main ── */}
        <div className="al-main">
          <div className="al-topbar">
            <div className="al-topbar-title">
              {currentItem?.icon} {currentItem?.label || "Admin Panel"}
            </div>
            <div className="al-topbar-right">
              <span className="al-admin-badge">Admin</span>
              <Link to="/" className="al-topbar-link">🌐 Store</Link>
              <button className="al-topbar-logout" onClick={handleLogout}>Sign Out</button>
            </div>
          </div>
          <div className="al-content"><Outlet /></div>
        </div>

        {/* ── Mobile More drawer ── */}
        {showMore && (
          <>
            <div className="al-more-overlay" onClick={() => setShowMore(false)} />
            <div className="al-more-drawer">
              {MOBILE_MORE.map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`al-more-row ${isActive(item.path) ? "active" : ""}`}
                  onClick={() => setShowMore(false)}
                >
                  <span className="al-more-row-icon">{item.icon}</span>
                  {item.label}
                </Link>
              ))}
              <div className="al-more-divider" />
              <button className="al-more-row" style={{ color:"#ef4444" }} onClick={handleLogout}>
                <span className="al-more-row-icon">🚪</span>
                Sign Out
              </button>
            </div>
          </>
        )}

        {/* ── Mobile Bottom Nav ── */}
        <nav className="al-bnav">
          {MOBILE_PRIMARY.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`al-bnav-tab ${isActive(item.path) ? "active" : ""}`}
              onClick={() => setShowMore(false)}
            >
              <span className="al-bnav-icon">{item.icon}</span>
              <span>{item.short}</span>
            </Link>
          ))}
          <button
            className={`al-bnav-tab ${moreIsActive || showMore ? "active" : ""}`}
            onClick={() => setShowMore(p => !p)}
          >
            <span className="al-bnav-icon">⋯</span>
            <span>More</span>
          </button>
        </nav>

      </div>
    </>
  );
}